/**
 * Cookie Manager Utility
 * Centralized cookie handling for the entire theme
 */

/**
 * Cookie utilities for managing browser cookies
 */
class CookieManager {
  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string | null} Cookie value or null if not found
   */
  static get(name) {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.indexOf(nameEQ) === 0) {
        return trimmedCookie.substring(nameEQ.length);
      }
    }

    return null;
  }

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiration
   * @param {string} [domain] - Cookie domain
   */
  static set(name, value, days, domain) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;

    const domainPart = domain ? `;domain=${domain}` : "";
    document.cookie = `${name}=${value};${expires};path=/${domainPart}`;
  }

  /**
   * Set a session cookie. The browser drops it when the tab session ends.
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {string} [domain] - Cookie domain
   */
  static setSession(name, value, domain) {
    const domainPart = domain ? `;domain=${domain}` : "";
    document.cookie = `${name}=${value};path=/${domainPart}`;
  }

  /**
   * Delete a cookie
   * @param {string} name - Cookie name
   * @param {string} [domain] - Cookie domain
   */
  static delete(name, domain) {
    this.set(name, "", -1, domain);
  }

  /**
   * Check if a cookie exists
   * @param {string} name - Cookie name
   * @returns {boolean} True if cookie exists, false otherwise
   */
  static has(name) {
    return this.get(name) !== null;
  }

  /**
   * Get all cookies as an object
   * @returns {Object.<string, string>} Object with cookie names as keys and values
   */
  static getAll() {
    /** @type {Record<string, string>} */
    const cookies = {};
    const cookieStrings = document.cookie.split(";");

    for (const cookie of cookieStrings) {
      const trimmedCookie = cookie.trim();
      const [name, ...valueParts] = trimmedCookie.split("=");
      if (name) {
        cookies[name] = valueParts.join("=");
      }
    }

    return cookies;
  }
}

// Make available globally via Theme object
if (typeof Theme !== "undefined") {
  Theme.CookieManager = CookieManager;
} else {
  // @ts-ignore - Theme object is initialized elsewhere, we're only adding CookieManager
  window.Theme = window.Theme || {};
  window.Theme.CookieManager = CookieManager;
}

// Export for ES6 module usage
export { CookieManager };
